import { DatabasesDisplayGrid_columns, TablesDisplayGrid_columns } from '@tableau/data-catalog-client-components';
import { DataGrid, GridSelection } from '@tableau/data-grid-3-wrapper';
import * as _ from 'underscore';

import { ServiceLocator } from '../../../app/ServiceLocator';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { ExplorableItemType, getKeyForExplorableItem } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { ServerService } from '../../../ts/services/ServerService';
import { ItemsSet } from '../../ItemsSet';
import {
  DataRoleSubspaceName,
  DatasourceSubspaceName,
  FlowSubspaceName,
  isAllSitesSpace,
  isBackgroundJobsSpace,
  isDataRoleSpace,
  isDatasourceSpace,
  isFlowSpace,
  isProjectSpace,
  isRecentsSpace,
  isScheduleSpace,
  isSiteSpace,
  isSiteTasksSpace,
  isSiteUserDataAlertsSubSpace,
  isSiteUserSpace,
  isWorkbookSpace,
  ScheduleSubspaceName,
  SiteTasksSubspaceName,
  SpaceInfo,
  WorkbookSubspaceName,
} from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ColumnType, ListViewGridColumnGenerator } from './ListViewGridColumnGenerator';
import { CommonColumn } from './types/Columns';

export interface ListViewGridContentGeneratorBaseParams {
  readonly items: ReadonlyArray<IExplorableItemTypeInfo>;
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly selectItem: (item: IContentInfo) => void;
  readonly deselectItem: (item: IContentInfo) => void;
  readonly requestProjectContentsMissingSlice: (start: number, end: number) => void;
  readonly spaceInfo: SpaceInfo;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly angularContext: AngularContext;
  readonly currentSort: ReadonlyArray<IOrderClause>;
  readonly changeSort: (field: string) => void;
  readonly setSortOrder: (order: ReadonlyArray<IOrderClause>) => void;
  readonly isSessionSiteOrServerAdmin: boolean;
  readonly displaySchedulesInClientTimezone: boolean;
  readonly ignoreExtractTaskPriority: boolean;
  readonly displayScheduleDescriptionAsName: boolean;
  readonly inServerArea: boolean;
  readonly isGuestEnabled: boolean;
  readonly urls: SpacesSiteUrls;
}

export interface FavoriteActionsParams {
  readonly addContentFavorite: (contentId: string, contentType: ExploreContentApi_ContentType) => any;
  readonly removeContentFavorite: (contentId: string, contentIdForFavoriting: string, contentType: ExploreContentApi_ContentType, contentName: string) => any;
}

export type ListViewGridContentGeneratorParams = ListViewGridContentGeneratorBaseParams & FavoriteActionsParams;

export const ColumnsForContentType: Record<ExplorableItemType, ColumnType[]> = {
  'project': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ProjectsCount, CommonColumn.WorkbooksCount, CommonColumn.ViewsCount, CommonColumn.DatasourcesCount, CommonColumn.Owner, CommonColumn.Created],
  'workbook': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.Hits, CommonColumn.SheetsCount, CommonColumn.Size, CommonColumn.Project, CommonColumn.Owner, CommonColumn.Modified, CommonColumn.Alert],
  'datasource': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.DataQualityIndicator, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.Hits, CommonColumn.DatasourceInWorkbooksCount, CommonColumn.ConnectsTo, CommonColumn.Project, CommonColumn.Owner, CommonColumn.ExtractStatus, CommonColumn.Alert],
  'dataRole': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.Project, CommonColumn.Owner, CommonColumn.Modified],
  'view': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name,CommonColumn.Hits, CommonColumn.Workbook, CommonColumn.SheetNumber, CommonColumn.Project, CommonColumn.Owner, CommonColumn.Modified],
  'dataconnection': [CommonColumn.Select, CommonColumn.ConnectsTo, CommonColumn.ConnectionType, CommonColumn.Authentication, CommonColumn.Username, CommonColumn.Datasource],
  'flow': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.DataQualityIndicator, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.Project, CommonColumn.Owner, CommonColumn.Modified],
  'subscription': [CommonColumn.Select, CommonColumn.SubscribedTo, CommonColumn.Subject, CommonColumn.Schedule, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
  'extractTask': [],
  'connection': [],
  'remoteRefreshSchedule': [],
  'connectedWorkbook': [],
  'flowOutputStepRun': [],
  'backgroundJob': [CommonColumn.Select, CommonColumn.Id, CommonColumn.Status, CommonColumn.BackgroundJobPriority, CommonColumn.BackgroundJobType, CommonColumn.JobRequestedTime, CommonColumn.CurrentRunTime, CommonColumn.CurrentQueueTime, CommonColumn.AverageRunTime, CommonColumn.AverageQueueTime],
  'database': [ CommonColumn.Select, ...DatabasesDisplayGrid_columns ],
  'table': [ CommonColumn.Select, ...TablesDisplayGrid_columns ],
  'metric': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.MetricValue, CommonColumn.MetricConnectedView, CommonColumn.Owner, CommonColumn.Modified, CommonColumn.MetricUpdated ],
  'siteuser': [CommonColumn.Select, CommonColumn.SiteUserName],
  'serveruser': [],
  'recommendedView': [],
  'dataalert': [CommonColumn.Select, CommonColumn.DataAlertSubject, CommonColumn.Owner, CommonColumn.DataAlertView, CommonColumn.DataAlertRecepient, CommonColumn.DataAlertVisibleTo, CommonColumn.DataAlertEmailSent,  CommonColumn.DataAlertLastChecked, CommonColumn.DataAlertLast],
  'site': [CommonColumn.Select, CommonColumn.SiteNameLink, CommonColumn.SiteUserCount, CommonColumn.SiteAdminCount, CommonColumn.SiteUserQuota, CommonColumn.SiteStorageUsed, CommonColumn.SiteStorageQuota, CommonColumn.SiteStatus, CommonColumn.SiteMetricsEnabled, CommonColumn.SiteAuthoringEnabled, CommonColumn.SiteSheetImageEnabled, CommonColumn.SiteEditMembership, CommonColumn.SiteExtractEncryption, CommonColumn.SiteGuestAccessEnabled, CommonColumn.Created],
  'schedule': [CommonColumn.Select, CommonColumn.ScheduleName, CommonColumn.ScheduleFrequency, CommonColumn.ScheduleTaskType, CommonColumn.ScheduleTasks, CommonColumn.ScheduleExecution, CommonColumn.ScheduleNextRunAt],
  'runFlowTask': [],
};

export const ColumnsForMixedContentTypes: ColumnType[] = [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.Owner, CommonColumn.Modified];

export const ColumnsForWorkbookSubspaces: Record<WorkbookSubspaceName, ColumnType[]> = {
  'datasources': [CommonColumn.Select, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.ConnectsTo, CommonColumn.ExtractStatusOnly, CommonColumn.Alert],
  'dataconnections': [CommonColumn.Select, CommonColumn.ConnectsTo, CommonColumn.ConnectionType, CommonColumn.Authentication, CommonColumn.Username, CommonColumn.Datasource],
  'views': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.Hits, CommonColumn.SheetNumber],
  'subscriptions': [CommonColumn.Select, CommonColumn.UsernameForSubscription, CommonColumn.SubscribedTo, CommonColumn.Subject, CommonColumn.Schedule, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
  'extractTasks': [CommonColumn.Select, CommonColumn.TaskType, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.State, CommonColumn.LastRunAt, CommonColumn.RunNextAt],
  'lineage': [],
};

export const ColumnsForDataRoleSubspaces: Record<DataRoleSubspaceName, ColumnType[]> = {
  'definition': [],
  'connectedFlows': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.Name, CommonColumn.Hits, CommonColumn.SheetsCount, CommonColumn.Size, CommonColumn.Project, CommonColumn.Owner, CommonColumn.Modified, CommonColumn.Alert],
};

export const ColumnsForDatasourceSubspaces: Record<DatasourceSubspaceName, ColumnType[]> = {
  'lineage': [],
  'nlp': [],
  'connections': [CommonColumn.Select, CommonColumn.ConnectsTo, CommonColumn.ConnectionType, CommonColumn.Authentication, CommonColumn.Username],
  'extractRefreshes': [CommonColumn.Select, CommonColumn.TaskType, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.LastRunAt, CommonColumn.RunNextAt],
  'remoteRefreshSchedules': [CommonColumn.Select, CommonColumn.RemoteRefreshType, CommonColumn.Definition, CommonColumn.RemoteRefreshPriority, CommonColumn.LastRunAt, CommonColumn.RunNextAt],
  'connectedWorkbooks': [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.Name, CommonColumn.Hits, CommonColumn.SheetsCount, CommonColumn.Size, CommonColumn.Project, CommonColumn.Owner, CommonColumn.Modified, CommonColumn.Alert],
  'askDataUsageAnalytics': [],
};

export const ColumnsForRecentsViewsSpace: ColumnType[] = [CommonColumn.Select, CommonColumn.Favorite, CommonColumn.ContentType, CommonColumn.Name, CommonColumn.Workbook, CommonColumn.LastAccessedAt, CommonColumn.Project, CommonColumn.Owner, CommonColumn.Modified];

export const ColumnsForFlowSubspaces: Record<FlowSubspaceName, ColumnType[]> = {
  'overview': [],
  'connections': [],
  'scheduledTasks': [],
  'runHistory': [CommonColumn.OutputStep, CommonColumn.RunType, CommonColumn.RunStartedAt, CommonColumn.RunEndedAt, CommonColumn.RunDuration, CommonColumn.RunStatus, CommonColumn.RunRowsProduced, CommonColumn.RunErrors],
  'lineage': [],
};

export const ColumnsForScheduleSubspaces: Record<ScheduleSubspaceName, ColumnType[]> = {
  'details': [],
  'flows': [CommonColumn.Select, CommonColumn.FlowName, CommonColumn.OutputSteps, CommonColumn.Priority, CommonColumn.RunFlowTasksStatus, CommonColumn.SiteName, CommonColumn.LastRunAtFlows, CommonColumn.RunNextAtFlows, CommonColumn.RunErrors],
  'extractRefreshes': [CommonColumn.Select, CommonColumn.WorkbookDatasourceName, CommonColumn.TaskType, CommonColumn.Priority, CommonColumn.SiteName, CommonColumn.LastRunAt, CommonColumn.RunNextAt],
  'subscriptions': [CommonColumn.Select, CommonColumn.UsernameForSubscription, CommonColumn.SubscribedTo, CommonColumn.Subject, CommonColumn.SiteName, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
};

export const ColumnsForTaskSubspaces: Record<SiteTasksSubspaceName, ColumnType[]> = {
  'extractRefreshes': [CommonColumn.Select, CommonColumn.WorkbookDatasourceName, CommonColumn.TaskType, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.LastRunAt, CommonColumn.RunNextAt],
  'runFlowTasks': [CommonColumn.Select, CommonColumn.FlowName, CommonColumn.OutputSteps, CommonColumn.Schedule, CommonColumn.Priority, CommonColumn.RunFlowTasksStatus, CommonColumn.LastRunAtFlows, CommonColumn.RunNextAtFlows, CommonColumn.RunErrors],
  'subscriptions': [CommonColumn.Select, CommonColumn.UsernameForSubscription, CommonColumn.SubscribedTo, CommonColumn.Subject, CommonColumn.Schedule, CommonColumn.SubscribedBy, CommonColumn.LastUpdate, CommonColumn.RunNextAt],
  'alerts': [CommonColumn.Select, CommonColumn.DataAlertSubject, CommonColumn.Owner, CommonColumn.DataAlertView, CommonColumn.DataAlertRecepient, CommonColumn.DataAlertVisibleTo, CommonColumn.DataAlertEmailSent,  CommonColumn.DataAlertLastChecked, CommonColumn.DataAlertLast],
};

export class ListViewGridContentGenerator {

  private columnGenerator: ListViewGridColumnGenerator;

  private params: ListViewGridContentGeneratorParams;
  private rows: DataGrid.Row[] = [];

  public cols: DataGrid.Column[] = [];
  public rowStore = (): DataGrid.RowStore => {
    return {
      getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {

        // check to see if we are missing any items
        // and potentially make call to server to get more
        const indexOfUndefined = _.indexOf(this.params.items, undefined, start);
        if (indexOfUndefined >= start && indexOfUndefined <= end) {
          this.params.requestProjectContentsMissingSlice(indexOfUndefined, end);
        }

        rowHandler(this.rows);
      }
    };
  }

  // Simply match the grid selection to our array of selected items
  public selection = (): GridSelection => {
    return {
      isSelected: (cell: DataGrid.Cell): boolean => {
        if (cell.rowIdx > -1 && cell.rowIdx < this.params.items.length) {
          const item = this.params.items[cell.rowIdx];
          return !!this.params.selectedItems[getKeyForExplorableItem(item)];
        }
    }};
  }

  public constructor(params: ListViewGridContentGeneratorParams) {
    this.params = params;
    this.initializeRowsAndColumns();
  }

  public generateContent(params: ListViewGridContentGeneratorParams): void {

    // We only want to skip regenerating the columns if the only thing that changed
    // is the selectedItems.

    const selectedItemsHaveChanged = this.params.selectedItems !== params.selectedItems;
    const otherContentPropsHaveChanged =
      this.params.items !== params.items ||
      this.params.contentFilter !== params.contentFilter ||
      this.params.currentSort !== params.currentSort;
    const onlySelectedItemsHaveChanged = selectedItemsHaveChanged && !otherContentPropsHaveChanged;

    this.params = params;

    if (onlySelectedItemsHaveChanged) {
      // We still need to update the column generator so that it has the updated selected items array.
      this.columnGenerator.updateParams({
        selectedItems: this.params.selectedItems
      });
    } else {
      this.initializeRowsAndColumns();
    }
  }

  private initializeRowsAndColumns(): void {

    // Row data gets reset when props are updated
    this.rows = new Array(this.params.items.length);
    this.params.items.forEach((item: IContentInfo, index: number) => {
      this.rows[index] = {
        key: item.id,
        data: {...item, rowIndex: index}  // this has to be rowIndex instead of index so it doesn't override the 'index' in data TFSID944970
      };
    });

    /*
     * Guidance toward future refactoring (DRBR, 2019-03-18):
     *
     * My goal is to easily add/define columns for a new content type / subplace. I want those columns
     * to be defined in the module that my team owns (the unique columns, at least). I also want a clear
     * place to list the columns for explorer grids displaying my content type across all the various
     * pages where that grid might appear (preferably, my module would own that listing as well).
     * It seems natural to have the column list be part of the ContentDefinition object for each
     * content type. If the ContentGenerator can reference the column listing from there,
     * the ContentDefinition could easily also point to a list defined in a different module.
     *
     * An idea to make this happen is to pivot this code so that each column list lives in a
     * `listViewColumnTypes(spaceInfo) function on its content type's definition object (in
     * ContentDefinitions.ts). The ContentGenerator would fetch the content definition for whichever
     * content type we're currently viewing via some helper code in SpaceInfo.ts (there would still
     * need to be a special case for mixed content), then pass the space info into the
     * `listViewColumnTypes` function, which would return the correct columns.
     *
     * In addition, columns can now be defined as standalone objects or factory functions, for cases
     * where their implementations are sufficiently unique enough to warrant not living in the
     * ColumnGenerator file itself. We can pull the column definitions out from the giant switch
     * statement, as appropriate, by changing each one into a function that takes a subset of the
     * ColumnGenerator params, or just a static column object. Then, as long as their params are a
     * subset of the ones provided by the ColumnGenerator, special columns can be defined in any module.
     */

    // Filtering on one specific type, simply returning that list of columns for that contenttype
    // Otherwise return the mixed content type columns
    // This assumes the content types are unique in contentFilter
    let columnTypes: ColumnType[] = [];
    const showingMixedContent = this.params.contentFilter.length > 1;

    if (isWorkbookSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForWorkbookSubspaces[this.params.spaceInfo.subspaceName as WorkbookSubspaceName];
    } else if (isDatasourceSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForDatasourceSubspaces[this.params.spaceInfo.subspaceName as DatasourceSubspaceName];
    } else if (isRecentsSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForRecentsViewsSpace;
    } else if (isFlowSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForFlowSubspaces[this.params.spaceInfo.subspaceName as FlowSubspaceName];
    } else if (isDataRoleSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForDataRoleSubspaces[this.params.spaceInfo.subspaceName as DataRoleSubspaceName];
    } else if (isScheduleSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForScheduleSubspaces[this.params.spaceInfo.subspaceName as ScheduleSubspaceName];
      if (!this.params.inServerArea) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.SiteName);
      }
    } else if (isSiteTasksSpace(this.params.spaceInfo)) {
      columnTypes = ColumnsForTaskSubspaces[this.params.spaceInfo.subspaceName as SiteTasksSubspaceName];
    } else if (showingMixedContent) {
      columnTypes = ColumnsForMixedContentTypes;
    } else if(this.params.contentFilter.length === 1) {
      columnTypes = this.getSpecificContentTypeColumns(this.params.contentFilter[0]);
    }

    // If we are in the site space looking at projects and not an admin remove the selection column
    if (this.params.contentFilter.length === 1 && this.params.contentFilter[0] === 'project' && isSiteSpace(this.params.spaceInfo) && !this.params.isSessionSiteOrServerAdmin) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Select);
    }

    // remove the extract task priority column if we are told to ignore it
    if (this.params.ignoreExtractTaskPriority) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Priority);
    }

    const { items, requestProjectContentsMissingSlice, ignoreExtractTaskPriority, ...columnGeneratorParams} = this.params;
    this.columnGenerator = new ListViewGridColumnGenerator({
      ...columnGeneratorParams,
      showingMixedContent: showingMixedContent,
      displayingItems: items.length > 0
    });

    this.cols = _.compact(columnTypes.map((columnType) => this.columnGenerator.getColumn(columnType)));
  }

  private getSpecificContentTypeColumns(contentType: ExplorableItemType): ColumnType[] {

    let columnTypes = ColumnsForContentType[contentType];

    // Don't show the project column if we are in a project space
    if (isProjectSpace(this.params.spaceInfo)) {
      columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Project);
    }

    if (isSiteUserSpace(this.params.spaceInfo)) {
      // Data alerts subspace isn't list of alerts 'owned' by the user.
      // It is list of alerts being received by the user, so the owner column still makes sense.
      if (!isSiteUserDataAlertsSubSpace(this.params.spaceInfo)) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.Owner);
      }

      if(!FeatureFlags.displayDataAlertPrivacy) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.DataAlertVisibleTo);
      }
    }

    if (isAllSitesSpace(this.params.spaceInfo)) {
      const serverService: ServerService = ServiceLocator.get<ServerService>('ServerService');
      if (!serverService.isExtractEncryptionEnabledOmniSwitch()) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.SiteExtractEncryption);
      }

      if (!FeatureFlags.guestAccountPerSiteEnabled || !this.params.isGuestEnabled) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.SiteGuestAccessEnabled);
      }
    }

    if (isBackgroundJobsSpace(this.params.spaceInfo)) {
      if (!FeatureFlags.jobManagement194UIEnabled) {
        columnTypes = _.filter(columnTypes, (columnType) => columnType !== CommonColumn.AverageQueueTime && columnType !== CommonColumn.AverageRunTime);
      }
    }

    if (contentType !== 'datasource' && contentType !== 'flow') {
      columnTypes = _.filter(columnTypes, (columnType) => (columnType) !== CommonColumn.DataQualityIndicator);
    }

    return columnTypes;
  }
}
